import React from 'react';
import { Network, MapPin, FileText, CreditCard, Shield, Building, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { features } from '../data/mockData';

const FeaturesSection = () => {
  const getFeatureIcon = (iconName) => {
    const iconMap = {
      Network: Network,
      MapPin: MapPin,
      FileText: FileText,
      CreditCard: CreditCard,
      Shield: Shield,
      Building: Building
    };
    const Icon = iconMap[iconName] || Network;
    return <Icon className="h-6 w-6" />;
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge variant="secondary" className="mb-4 bg-blue-100 text-blue-800 border-blue-200">
            Why Choose SendMyParcel
          </Badge>
          <h2 className="text-4xl font-bold text-slate-900 mb-6">
            Everything You Need for Seamless Shipping
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Advanced features and reliable service that make SendMyParcel the preferred choice for 
            businesses and individuals across India.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {features.map((feature) => (
            <Card key={feature.id} className="group bg-white rounded-2xl shadow-sm hover:shadow-2xl transition-all duration-300 border border-gray-100/50 hover:border-[#F17221]/30 transform hover:-translate-y-2 overflow-hidden">
              <CardHeader className="pb-4 relative">
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-[#0A1128]/5 to-transparent rounded-bl-full -z-10 transition-all group-hover:scale-110"></div>
                <div className="bg-[#0A1128]/5 p-4 rounded-2xl inline-block mb-4 group-hover:bg-[#F17221]/10 transition-colors">
                  <div className="text-[#0A1128] group-hover:text-[#F17221] transition-colors">
                    {getFeatureIcon(feature.icon)}
                  </div>
                </div>
                <CardTitle className="text-xl font-bold text-slate-900 group-hover:text-[#0A1128] transition-colors">
                  {feature.title}
                </CardTitle>
              </CardHeader>
              
              <CardContent>
                <p className="text-slate-600 leading-relaxed font-medium">
                  {feature.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Benefits Highlight */}
        <div className="bg-[#0A1128] rounded-3xl p-8 lg:p-12 text-white shadow-2xl relative overflow-hidden">
          <div className="absolute top-[-20%] right-[-10%] w-[50%] h-[150%] bg-[#F17221]/10 blur-[100px] rounded-full point-events-none"></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-bold mb-6">
                Join 10,000+ Happy Customers
              </h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                  <span>Free account setup with no hidden fees</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                  <span>Dedicated account manager for businesses</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                  <span>Bulk shipping discounts up to 40%</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                  <span>API integration for seamless workflow</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                  <span>Priority customer support</span>
                </div>
              </div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="grid grid-cols-2 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-orange-400 mb-2">98%</div>
                  <div className="text-sm opacity-90">Customer Satisfaction</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-orange-400 mb-2">24/7</div>
                  <div className="text-sm opacity-90">Customer Support</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-orange-400 mb-2">4.8/5</div>
                  <div className="text-sm opacity-90">Customer Rating</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-orange-400 mb-2">5+</div>
                  <div className="text-sm opacity-90">Carrier Partners</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;